import { graphqlPost } from "api/graphqlClient";
import { getByExecutionTypeName } from "components/Icon/Icon";
import { FeExecutionType } from "config/config";
import { Markdown } from "components/Form/components/Markdown/Markdown";
import { ExecutionStatusBadge } from "components/StatusBadge/ExecutionStatusBadge";
import { ExecutionStatus } from "graphql/generated/enums";
import { useCallback, useEffect, useRef, useState } from "react";
import { serializeStructuredData } from "utils/valueTransformers";
import { useStructuredDataDisplayMode } from "utils/structuredDataDisplayMode";
import { DashboardRecordLink } from "../DashboardRecordLink";
import { DashboardLiveWidgetShell } from "../DashboardLiveWidgetShell";
import { describeWidgetFetchError } from "../dashboardWidgetError";
import { WidgetComponentProps } from "../types";
import { useDashboardLiveRefresh } from "../useDashboardLiveRefresh";

export interface ExecutionOutputWidgetConfig {
    /** Id of a specific execution to display. */
    execution_id?: string;
    /** Id of the flow whose latest execution to display. */
    flow_id?: string;
    /**
     * When true, poll for updates. For `execution_id`, polling stops once the
     * execution has ended. For `flow_id`, polling continues so a newly started
     * execution of the same flow is picked up. Defaults to true.
     */
    live?: boolean;
    /**
     * Which section to show. Defaults to "output".
     * "logs" shows the last N log lines; "output" shows the output_value JSON;
     * "message" renders execution.message as markdown.
     */
    show?: "output" | "logs" | "message";
    /** For show="logs": maximum number of log lines to fetch. Defaults to 20. */
    log_limit?: number;
}

interface ExecutionData {
    id: string;
    name: string;
    type?: string;
    status: ExecutionStatus;
    message?: string;
    output_value?: unknown;
}

interface LogEntry {
    id: string;
    level: string;
    subject: string;
    data?: string;
    created_at: string;
}

const POLL_INTERVAL_MS = 3000;

const RUNNING_STATUSES: ExecutionStatus[] = [
    "AT_ENGINE",
    "WAITING_ENGINE",
    "WAITING_DEPENDENCY",
    "WAITING_LOCK",
    "WAITING_RECORD",
    "WAITING_RESPONSE",
    "PAUSED",
    "SCHEDULED",
];

/**
 * `execution_output` widget — shows the status and output (or recent logs) of an
 * execution. Configure either `execution_id` (fixed execution) or `flow_id` (latest
 * execution of that flow). When `live` is true, polls for updates; polling stops
 * after the execution ends only in `execution_id` mode.
 *
 * Uses a raw GraphQL query rather than the full Redux-connected `ExecutionInputOutputValueTab`
 * and `LogsListing` components, which are too deeply coupled to the ResourcesSingle screen.
 */
export const ExecutionOutputWidget = ({
    widget,
    config,
}: WidgetComponentProps<ExecutionOutputWidgetConfig>) => {
    const [execution, setExecution] = useState<ExecutionData | null>(null);
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setLoading] = useState(true);
    const isMounted = useRef(true);
    // Respect the global JSON/YAML structured-data display preference (set from
    // any structured-data editor's toolbar toggle). The dashboard widget is a
    // passive surface — it follows the shared preference rather than carrying its
    // own interactive toolbar.
    const displayMode = useStructuredDataDisplayMode();

    const executionId = config?.execution_id;
    const flowId = config?.flow_id;
    const show = config?.show ?? "output";
    const logLimit = config?.log_limit ?? 20;
    const live = config?.live !== false;
    const isFlowMode = Boolean(flowId) && !executionId;

    const fetchExecution = useCallback(async (): Promise<boolean> => {
        if (!executionId && !flowId) {
            if (isMounted.current) {
                setError("Widget config is missing execution_id or flow_id.");
                setLoading(false);
            }
            return false;
        }

        const logsField =
            show === "logs"
                ? `record_log_plain_list_record_id(limit: ${logLimit}, order: "-created_at") {
                    id
                    level
                    subject
                    data
                    created_at
                }`
                : "";

        const query = isFlowMode
            ? `query DashboardExecutionOutput($id: String!) {
                flow(by: "id", id: $id) {
                    execution_plain_list_flow_id(limit: 1, order: "-created_at") {
                        id
                        name
                        type
                        status
                        message
                        output_value
                        ${logsField}
                    }
                }
            }`
            : `query DashboardExecutionOutput($id: String!) {
                execution(by: "id", id: $id) {
                    id
                    name
                    type
                    status
                    message
                    output_value
                    ${logsField}
                }
            }`;

        try {
            const response = await graphqlPost({
                query,
                variables: { id: isFlowMode ? flowId : executionId },
            });
            const data = isFlowMode
                ? (() => {
                      const list = response?.data?.flow?.execution_plain_list_flow_id;
                      return Array.isArray(list) ? list[0] ?? null : null;
                  })()
                : response?.data?.execution ?? null;
            if (!isMounted.current) {
                return false;
            }
            if (!data) {
                setError(
                    isFlowMode ? "No executions found for this flow." : "Execution not found."
                );
                setLoading(false);
                return false;
            }
            setExecution(data);
            if (show === "logs" && Array.isArray(data.record_log_plain_list_record_id)) {
                setLogs([...data.record_log_plain_list_record_id].reverse());
            }
            setError(null);
            setLoading(false);
            return true;
        } catch (err: any) {
            if (!isMounted.current) {
                return false;
            }
            setError(describeWidgetFetchError(err, "Failed to load execution."));
            setLoading(false);
            return false;
        }
    }, [executionId, flowId, isFlowMode, show, logLimit]);

    const shouldPoll = isFlowMode
        ? true
        : execution
          ? RUNNING_STATUSES.includes(execution.status)
          : true;

    const { lastUpdatedAt, refresh, isRefreshing } = useDashboardLiveRefresh({
        live,
        pollIntervalMs: POLL_INTERVAL_MS,
        shouldPoll,
        fetch: fetchExecution,
    });

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        setLoading(true);
    }, [fetchExecution]);

    let content;
    // Keep the last loaded execution on a failed re-fetch; surface a stale
    // re-fetch error inline on the live bar instead of blanking the widget.
    if (error && !execution) {
        content = <div className="DashboardWidget__error">{error}</div>;
    } else if (isLoading) {
        content = <div className="DashboardWidget__empty">Loading…</div>;
    } else if (!execution) {
        content = <div className="DashboardWidget__empty">No execution.</div>;
    } else {
        content = (
            <div className="DashboardExecutionOutput">
                <div className="DashboardExecutionOutput__header">
                    <DashboardRecordLink
                        id={execution.id}
                        name={execution.name}
                        recordType="execution"
                        icon={
                            execution.type
                                ? getByExecutionTypeName(
                                      execution.type.toLowerCase() as FeExecutionType
                                  )
                                : undefined
                        }
                    />
                    <ExecutionStatusBadge data={execution as any} size="" />
                </div>

                {execution.message && show !== "message" && (
                    <p className="DashboardExecutionOutput__message">{execution.message}</p>
                )}

                {show === "message" && (
                    execution.message ? (
                        <Markdown
                            markdownString={execution.message}
                            uniqueKey={`${widget.uid}-message`}
                        />
                    ) : (
                        <div className="DashboardWidget__empty">No message yet.</div>
                    )
                )}

                {show === "output" && execution.output_value != null && (
                    <Markdown
                        markdownString={`\`\`\`${displayMode}\n${serializeStructuredData(
                            execution.output_value,
                            displayMode
                        )}\n\`\`\``}
                        uniqueKey={widget.uid}
                        canCopy
                    />
                )}

                {show === "logs" && logs.length > 0 && (
                    <ul className="DashboardExecutionOutput__logs">
                        {logs.map((log) => (
                            <li
                                key={log.id}
                                className={`DashboardExecutionOutput__log DashboardExecutionOutput__log--${log.level?.toLowerCase()}`}
                            >
                                <span className="DashboardExecutionOutput__log-level">{log.level}</span>
                                <span className="DashboardExecutionOutput__log-msg">
                                    {log.subject || log.data || "—"}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}

                {show === "output" && execution.output_value == null && (
                    <div className="DashboardWidget__empty">No output yet.</div>
                )}

                {show === "logs" && logs.length === 0 && (
                    <div className="DashboardWidget__empty">No logs yet.</div>
                )}
            </div>
        );
    }

    const showManualRefresh = live && !isFlowMode && !shouldPoll;

    return (
        <DashboardLiveWidgetShell
            live={live}
            lastUpdatedAt={lastUpdatedAt}
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            showManualRefresh={showManualRefresh}
            refreshLabel="Refresh now"
            error={error}
        >
            {content}
        </DashboardLiveWidgetShell>
    );
};
